import React, { useState } from 'react';
import {
    DollarSign,
    Calendar,
    Clock,
    CheckCircle2,
    Plus,
    FileText,
    Wallet,
    TrendingUp,
    History,
    X,
    AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePayments } from '../../contexts/PaymentContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

import ContractOnboarding from './ContractOnboarding';
import TerminationOnboarding from './TerminationOnboarding';
import RenewalOnboarding from './RenewalOnboarding';
import LiquidationOnboarding from './LiquidationOnboarding';
import DataUpdateOnboarding from './DataUpdateOnboarding';

const DataField = ({ label, section, field, value, tempData, onChange, isEditable, readOnly = false, dropdownOptions = null }) => (
    <div className="w-full">
        <label className="text-xs text-gray-500 uppercase font-bold mb-1 block">{label}</label>
        {isEditable && !readOnly ? (
            dropdownOptions ? (
                <select
                    value={tempData[section]?.[field] || ''}
                    onChange={(e) => onChange(section, field, e.target.value)}
                    className="w-full bg-gray-800 border-gray-700 text-white rounded-md p-2 focus:ring-yellow-500/50"
                >
                    {dropdownOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            ) : (
                <Input
                    value={tempData[section]?.[field] || ''}
                    onChange={(e) => onChange(section, field, e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white focus:ring-yellow-500/50"
                />
            )
        ) : (
            <p className="text-white bg-gray-900/50 p-3 rounded-lg border border-gray-800 min-h-[46px] flex items-center">
                {value || '-'}
            </p>
        )}
    </div>
);

const ServiceProvision = () => {
    const { user, updateUser } = useAuth();
    const { payments, createRequest, getUserContract } = usePayments();

    // Check contract signature status
    const contractSigned = user?.staffDetails?.contractSigned;

    const handleContractComplete = async (timestamps) => {
        if (!user) return;
        console.log("Concluir Etapa clicked", timestamps);

        try {
            // Defensive access
            const currentStaffDetails = user.staffDetails || {};
            const currentContractData = currentStaffDetails.contractData || {};

            const result = await updateUser({
                staffDetails: {
                    ...currentStaffDetails,
                    contractSigned: true,
                    contractSignedAt: new Date().toISOString(),
                    contractData: {
                        ...currentContractData,
                        status: 'pending_docusign',
                        onboardingStartedAt: timestamps?.onboardingStartedAt,
                        termsAgreedAt: timestamps?.termsAgreedAt,
                        onboardingCompletedAt: timestamps?.onboardingCompletedAt || new Date().toISOString()
                    }
                }
            });

            if (!result.success) {
                console.error("Failed to update user contract:", result.error);
                alert("Erro ao salvar contrato: " + result.error);
            } else {
                console.log("Contract saved successfully!");
                alert("Etapa concluída com sucesso! Atualizando...");
                window.location.reload();
            }
        } catch (error) {
            console.error("Critical error in handleContractComplete:", error);
            alert("Erro crítico: " + error.message);
        }
    };

    const handleFinalSignature = async () => {
        if (!user) return;
        const currentStaffDetails = user.staffDetails || {};
        const currentContractData = currentStaffDetails.contractData || {};

        await updateUser({
            staffDetails: {
                ...currentStaffDetails,
                contractData: {
                    ...currentContractData,
                    status: 'active',
                    docusignSignedAt: new Date().toISOString()
                }
            }
        });
    };

    // Renewal Logic
    const renewalPending = user?.staffDetails?.contractData?.renewalPending;

    const handleRenewalComplete = async () => {
        if (!user) return;

        const currentStaffDetails = user.staffDetails || {};
        const currentContractData = currentStaffDetails.contractData || {};
        const renewalData = currentContractData.renewalData;

        if (!renewalData) return;

        // Calculate new end date based on duration
        const startDate = new Date(); // Renewal starts now
        const endDate = new Date(startDate);
        const duration = parseInt(renewalData.contractDuration);

        if (renewalData.contractUnit === 'days') {
            endDate.setDate(endDate.getDate() + duration);
        } else {
            endDate.setMonth(endDate.getMonth() + duration);
        }

        await updateUser({
            staffDetails: {
                ...currentStaffDetails,
                contractSigned: true,
                contractSignedAt: new Date().toISOString(),
                contractData: {
                    ...currentContractData,
                    status: 'active',
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    contractValue: parseFloat(renewalData.contractValue),
                    contractTerm: `${renewalData.contractDuration} ${renewalData.contractUnit === 'days' ? 'Dias' : 'Meses'}`,
                    contractDuration: duration,
                    contractUnit: renewalData.contractUnit,
                    jobFunction: renewalData.jobFunction,
                    serviceDescription: renewalData.serviceDescription,

                    // Clear renewal flags
                    renewalPending: false,
                    renewalData: null
                }
            },
            // Update root level access if role changed
            accessLevel: renewalData.newRole || user.accessLevel
        });

        // Force reload or ensure state updates
        window.location.reload();
    };

    if (renewalPending) {
        return <RenewalOnboarding user={user} onComplete={handleRenewalComplete} />;
    }

    if (!contractSigned) {
        return <ContractOnboarding user={user} onComplete={handleContractComplete} />;
    }

    const isPendingDocuSign = user?.staffDetails?.contractData?.status === 'pending_docusign';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDataModalOpen, setIsDataModalOpen] = useState(false);

    // Data Update Request Logic
    const dataUpdateRequest = user?.staffDetails?.dataUpdateRequest;
    const isDataRequestPending = dataUpdateRequest?.status === 'pending';
    const [dataRequestModalOpen, setDataRequestModalOpen] = useState(isDataRequestPending);
    const [showUpdateOnboarding, setShowUpdateOnboarding] = useState(false);

    // Effect to open modal if request is pending and we are not already in onboarding
    React.useEffect(() => {
        if (isDataRequestPending && !showUpdateOnboarding) {
            setDataRequestModalOpen(true);
        }
    }, [isDataRequestPending, showUpdateOnboarding]);

    // Temp state for editing
    const [tempData, setTempData] = useState({});

    // Main Dashboard State (Moved up to avoid conditional hook errors)
    const [activeTab, setActiveTab] = useState('personal');
    const [formData, setFormData] = useState({
        extraValue: '',
        observation: '',
        confirmedInvoice: false,
        skipStandardValue: false
    });

    // Initialize temp data when opening modal
    React.useEffect(() => {
        if (isDataModalOpen && user?.staffDetails) {
            setTempData(user.staffDetails);
        }
    }, [isDataModalOpen, user]);

    const handleSaveData = async () => {
        try {
            await updateUser({
                staffDetails: {
                    ...user.staffDetails,
                    personalData: { ...user.staffDetails.personalData, ...tempData.personalData },
                    businessData: { ...user.staffDetails.businessData, ...tempData.businessData },
                    bankingData: { ...user.staffDetails.bankingData, ...tempData.bankingData },
                    contractData: { ...user.staffDetails.contractData, ...tempData.contractData }
                }
            });
            // Don't close, let them finish update
            alert("Dados salvos com sucesso!");
        } catch (error) {
            console.error("Error saving data:", error);
            alert("Erro ao salvar dados.");
        }
    };

    const handleDataRequestComplete = async (updatedData) => {
        try {
            // UpdatedData should be the formData from the onboarding
            // Ensure we merge correctly
            const payload = {
                staffDetails: {
                    ...user.staffDetails,
                    ...updatedData, // Merge the updated sections
                    dataUpdateRequest: {
                        ...dataUpdateRequest,
                        status: 'completed',
                        completedAt: new Date().toISOString()
                    }
                }
            };

            await updateUser(payload);
            setShowUpdateOnboarding(false);
            window.location.reload();
        } catch (error) {
            alert('Erro ao concluir atualização: ' + error.message);
        }
    };

    // If showUpdateOnboarding is true, render that INSTEAD of the normal dashboard
    if (showUpdateOnboarding && isDataRequestPending) {
        return (
            <DataUpdateOnboarding
                user={user}
                requests={dataUpdateRequest?.types || []}
                onComplete={handleDataRequestComplete}
            />
        );
    }



    const contract = getUserContract(user?.discordId);
    const userPayments = payments.filter(p => p.userId === user?.discordId);

    // Calculate stats
    const totalPaid = userPayments
        .filter(p => p.status === 'approved')
        .reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);

    const pendingRequests = userPayments.filter(p => p.status === 'pending').length;

    // Calculate contract stats
    const today = new Date();
    // Default to a far future date if startDate is missing to prevent errors
    const startDateRaw = contract.startDate ? new Date(contract.startDate) : new Date();
    const startDate = isNaN(startDateRaw.getTime()) ? new Date() : startDateRaw;

    const endDate = contract.endDate ? new Date(contract.endDate) : null;

    let remainingDays = 999; // Default safe value
    let totalDuration = 0;
    const isDaysContract = contract.unit === 'days';

    if (endDate && !isNaN(endDate.getTime())) {
        const diffTime = endDate - today;
        remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (isDaysContract) {
            totalDuration = contract.duration || 0;
        } else {
            // Calculate total duration in months approx
            totalDuration = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
            // Ensure at least 1 month if it's new
            if (totalDuration < 1) totalDuration = 1;
        }
    }

    // Liquidation Logic
    // Liquidation Logic
    const isLiquidationPending = user?.staffDetails?.contractData?.liquidationPending;
    const isLiquidationSigned = user?.staffDetails?.contractData?.liquidationSignedAt;

    const handleLiquidationComplete = async (timestamps) => {
        if (!user) return;

        await updateUser({
            staffDetails: {
                ...user.staffDetails,
                contractData: {
                    ...user.staffDetails.contractData,
                    liquidationPending: false, // Clear pending status or keep it? Plan says keep access normal after.
                    liquidationSignedAt: new Date().toISOString(),
                    liquidationStartedAt: timestamps?.liquidationStartedAt,
                    liquidationViewedAt: timestamps?.liquidationViewedAt,
                    lastContractStartDate: timestamps?.lastContractStartDate // SAVE THE DATE
                }
            }
        });
        window.location.reload();
    };

    if (isLiquidationPending && !isLiquidationSigned) {
        return <LiquidationOnboarding user={user} onComplete={handleLiquidationComplete} />;
    }

    // Termination Logic
    const isTerminated = user?.staffDetails?.contractData?.status === 'terminated';
    const showTerminationFlow = !isTerminated && endDate && remainingDays <= 5 && remainingDays > -30; // Show if close to end or slightly passed, but not if indefinite

    const handleTermination = async (timestamps) => {
        if (!user) return;

        await updateUser({
            staffDetails: {
                ...user.staffDetails,
                contractData: {
                    ...user.staffDetails.contractData,
                    status: 'terminated',
                    terminationStartedAt: timestamps?.terminationStartedAt,
                    terminationViewedAt: timestamps?.terminationViewedAt,
                    terminationSignedAt: new Date().toISOString()
                }
            }
        });
        // Force refresh or state update will trigger the view change
    };

    if (isTerminated) {
        return (
            <div className="flex items-center justify-center p-4">
                <div className="max-w-md text-center space-y-6 animate-in fade-in zoom-in duration-700">
                    <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-12 h-12 text-red-500" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">Ciclo Encerrado</h1>
                    <p className="text-gray-400 text-lg">
                        Obrigado por fazer parte da nossa jornada.
                        Seu acesso ao painel foi encerrado conforme o Termo de Quitação.
                    </p>
                    <div className="p-4 bg-gray-900 rounded-lg border border-gray-800">
                        <p className="text-sm text-gray-500">
                            Higor Borges em nome do Grupo Capital, TronosSpace e Modder Produções!
                        </p>
                    </div>
                </div>
            </div>
        );

    }

    if (showTerminationFlow) {
        return <TerminationOnboarding user={user} onComplete={handleTermination} />;
    }

    // Calculate total duration in months approx - ensuring calculation is done
    const totalContractValue = isDaysContract
        ? (contract.monthlyValue || 0) // If days, assume the value is total or per period? Usually 'monthlyValue' is misnomer for 'contractValue'. Assuming it's the total value for the period.
        : (contract.monthlyValue || 0) * (totalDuration || 1);

    const handleSubmit = (e) => {
        e.preventDefault();

        const extra = parseFloat(formData.extraValue) || 0;
        const standard = formData.skipStandardValue ? 0 : (contract.monthlyValue || 0);
        const total = standard + extra;

        createRequest({
            standardValue: standard,
            extraValue: extra,
            totalValue: total,
            observation: formData.observation,
            invoiceSent: formData.confirmedInvoice
        });

        setIsModalOpen(false);
        setIsModalOpen(false);
        setFormData({ extraValue: '', observation: '', confirmedInvoice: false, skipStandardValue: false });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const StatusBadge = ({ status }) => {
        const styles = {
            pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
            approved: 'bg-green-500/10 text-green-500 border-green-500/20',
            rejected: 'bg-red-500/10 text-red-500 border-red-500/20'
        };

        const labels = {
            pending: 'Em Análise',
            approved: 'Aprovado',
            rejected: 'Rejeitado'
        };

        return (
            <Badge className={`${styles[status] || styles.pending} border`}>
                {labels[status] || status}
            </Badge>
        );
    };

    const renderDataTab = () => {
        // Determine if current tab is editable
        // Editable ONLY if request is pending AND this specific tab is requested
        const isEditable = isDataRequestPending && (
            dataUpdateRequest?.types?.includes(activeTab) ||
            (activeTab === 'banking' && dataUpdateRequest?.types?.includes('financial'))
        );

        const handleChange = (section, field, value) => {
            setTempData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        };

        const renderField = (props) => (
            <DataField
                {...props}
                tempData={tempData}
                onChange={handleChange}
                isEditable={isEditable}
            />
        );

        const details = user?.staffDetails || {};

        switch (activeTab) {
            case 'personal':
                return (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="text-xs text-gray-500 uppercase font-bold">Usuário Discord</label>
                                <div className="flex items-center gap-4 bg-gray-900/50 p-3 rounded-lg border border-gray-800">
                                    {user?.avatar && (
                                        <img
                                            src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                                            alt="Avatar"
                                            className="w-10 h-10 rounded-full"
                                        />
                                    )}
                                    <div>
                                        <p className="text-white font-bold">{user?.name || '-'}</p>
                                        <p className="text-xs text-gray-500">{user?.discordId || '-'}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs text-gray-500 uppercase font-bold">Email (Discord)</label>
                                <p className="text-white bg-gray-900/50 p-3 rounded-lg border border-gray-800">{user?.email || '-'}</p>
                            </div>
                            {renderField({ label: "Nome Completo", section: "personalData", field: "fullName", value: details.personalData?.fullName })}
                            {renderField({ label: "CPF", section: "personalData", field: "cpf", value: details.personalData?.cpf })}
                            {renderField({ label: "Data de Nascimento", section: "personalData", field: "birthDate", value: details.personalData?.birthDate })}
                            {renderField({ label: "Telefone", section: "personalData", field: "phone", value: details.personalData?.phone })}
                        </div>
                    </div>
                );
            case 'business':
                return (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 gap-4">
                            {renderField({ label: "Razão Social", section: "businessData", field: "businessName", value: details.businessData?.businessName })}
                            {renderField({ label: "CNPJ", section: "businessData", field: "cnpj", value: details.businessData?.cnpj })}
                            {renderField({ label: "CNAE Principal", section: "businessData", field: "primaryCnae", value: details.businessData?.primaryCnae })}
                            {renderField({ label: "Data de Abertura", section: "businessData", field: "openingDate", value: details.businessData?.openingDate })}
                            <div className="col-span-2">
                                {renderField({ label: "Endereço Completo", section: "businessData", field: "fullAddress", value: details.businessData?.fullAddress })}
                            </div>
                        </div>
                    </div>
                );
            case 'banking':
                return (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 gap-4">
                            {renderField({ label: "Banco", section: "bankingData", field: "bank", value: details.bankingData?.bank })}
                            {renderField({ label: "Tipo de Conta", section: "bankingData", field: "accountType", value: details.bankingData?.accountType })}
                            {renderField({ label: "Agência", section: "bankingData", field: "branch", value: details.bankingData?.branch })}
                            {renderField({ label: "Conta", section: "bankingData", field: "account", value: details.bankingData?.account })}

                            <div className="w-full">
                                <label className="text-xs text-gray-500 uppercase font-bold mb-1 block">Chave PIX</label>
                                <div className="flex gap-2">
                                    {isEditable ? (
                                        <select
                                            value={tempData.bankingData?.pixKeyType || ''}
                                            onChange={(e) => handleChange('bankingData', 'pixKeyType', e.target.value)}
                                            className="bg-gray-800 border-gray-700 text-white text-sm rounded-md focus:ring-yellow-500/50 p-2"
                                        >
                                            <option value="cpf">CPF</option>
                                            <option value="cnpj">CNPJ</option>
                                            <option value="email">Email</option>
                                            <option value="phone">Telefone</option>
                                            <option value="random">Aleatória</option>
                                        </select>
                                    ) : (
                                        <Badge variant="outline" className="h-10 px-3">{details.bankingData?.pixKeyType || 'Tipo'}</Badge>
                                    )}
                                    {isEditable ? (
                                        <Input
                                            value={tempData.bankingData?.pixKey || ''}
                                            onChange={(e) => handleChange('bankingData', 'pixKey', e.target.value)}
                                            className="bg-gray-800 border-gray-700 text-white focus:ring-yellow-500/50 flex-1"
                                        />
                                    ) : (
                                        <p className="text-white bg-gray-900/50 p-2 px-3 rounded-lg border border-gray-800 flex-1 flex items-center">
                                            {details.bankingData?.pixKey || '-'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {renderField({ label: "Titular", section: "bankingData", field: "holderName", value: details.bankingData?.holderName })}
                        </div>
                    </div>
                );
            case 'contract':
                return (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="grid grid-cols-2 gap-4">
                            {renderField({ label: "Função", section: "contractData", field: "jobFunction", value: details.contractData?.jobFunction, readOnly: true })}
                            {renderField({ label: "Prazo", section: "contractData", field: "contractTerm", value: details.contractData?.contractTerm, readOnly: true })}
                            <div className="w-full">
                                <label className="text-xs text-gray-500 uppercase font-bold mb-1 block">Valor Mensal</label>
                                <p className="text-white bg-gray-900/50 p-3 rounded-lg border border-gray-800 min-h-[46px] flex items-center">
                                    {details.contractData?.contractValue ? formatCurrency(details.contractData.contractValue) : '-'}
                                </p>
                            </div>
                            <div className="col-span-2">
                                {renderField({ label: "Descrição do Serviço", section: "contractData", field: "serviceDescription", value: details.contractData?.serviceDescription, readOnly: true })}
                            </div>
                        </div>
                        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-500">
                            * Dados contratuais não podem ser alterados pelo prestador. Entre em contato com o suporte se houver divergência.
                        </div>
                    </div>
                );
            case 'profile':
                return (
                    <div className="space-y-4 animate-in fade-in duration-300">
                        <div className="grid grid-cols-1 gap-4">
                            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                <h4 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> Importante
                                </h4>
                                <p className="text-sm text-blue-200/80">
                                    O Codinome é como você será identificado dentro do sistema (sidebar, rankings, etc).
                                    Escolha um nome profissional.
                                </p>
                            </div>
                            {renderField({ label: "Codinome (Nome de Exibição)", section: "businessData", field: "codename", value: details.businessData?.codename || details.codename })}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                        Prestação de Contas
                    </h1>
                    <p className="text-gray-400 mt-2">Gerencie seu contrato e solicitações de pagamento</p>
                </div>
                <Button
                    onClick={() => setIsDataModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg border-0"
                >
                    <FileText className="mr-2 h-4 w-4" />
                    Meus Dados
                </Button>
            </div>

            {/* Notification Area */}
            {isPendingDocuSign && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 flex flex-col md:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-blue-500/20 rounded-full text-blue-400">
                            <Clock className="h-6 w-6" />
                        </div>
                        <div>
                            <h4 className="text-blue-400 font-bold text-lg mb-1">Aguardando Assinatura DocuSign</h4>
                            <p className="text-blue-200/80 max-w-2xl">
                                Você receberá um e-mail oficial via <strong>DocuSign</strong> para realizar a assinatura digital jurídica do contrato.
                                Fique atento à sua caixa de entrada (e spam) do e-mail: <strong className="text-white">{user?.email}</strong>.
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={handleFinalSignature}
                        className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-500/20 whitespace-nowrap px-8 py-6 text-lg"
                    >
                        <CheckCircle2 className="mr-2 w-5 h-5" />
                        JÁ ASSINEI
                    </Button>
                </div>
            )}

            {remainingDays <= 30 && contract.status !== 'ended' && !isPendingDocuSign && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                    <div className="p-2 bg-yellow-500/10 rounded-lg text-yellow-500 mt-0.5">
                        <Clock className="h-5 w-5" />
                    </div>
                    <div>
                        <h4 className="text-yellow-500 font-bold">Aviso de Fim de Contrato</h4>
                        <p className="text-yellow-200/80 text-sm mt-1">
                            Um e-mail foi enviado notificando o fim da prestação em {remainingDays} dias.
                            Esta mensagem é um aviso de fim de contrato.
                        </p>
                    </div>
                </div>
            )}

            {/* Top Cards - Contract Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gray-900/50 border-gray-800 backdrop-blur-sm hover:border-blue-500/50 transition-all duration-300">
                    <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400">
                                <DollarSign className="h-6 w-6" />
                            </div>
                        </div>
                        <p className="text-sm text-gray-400">Valor Total do Contrato</p>
                        <h3 className="text-2xl font-bold text-white mt-1">{formatCurrency(totalContractValue)}</h3>
                        <p className="text-xs text-gray-500 mt-2">Valor total do contrato</p>
                    </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border-gray-800 backdrop-blur-sm hover:border-purple-500/50 transition-all duration-300">
                    <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 rounded-xl bg-purple-500/10 text-purple-400">
                                <Calendar className="h-6 w-6" />
                            </div>
                        </div>
                        <p className="text-sm text-gray-400">Tempo Restante</p>
                        <h3 className={`text-2xl font-bold mt-1 ${remainingDays < 0 ? 'text-red-500' : 'text-white'}`}>
                            {contract.endDate ? (remainingDays < 0 ? 'Vencido' : `${remainingDays} dias`) : 'A definir'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-2">
                            {contract.endDate ? `Vence em ${new Date(contract.endDate).toLocaleDateString()}` : 'Data final não definida'}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border-gray-800 backdrop-blur-sm hover:border-orange-500/50 transition-all duration-300">
                    <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 rounded-xl bg-orange-500/10 text-orange-400">
                                <CheckCircle2 className="h-6 w-6" />
                            </div>
                        </div>
                        <p className="text-sm text-gray-400">Status do Contrato</p>
                        <h3 className="text-2xl font-bold text-white mt-1 capitalize">
                            {contract.status === 'active' ? 'Vigente' : contract.status === 'ended' ? 'Finalizado' : 'Pendente'}
                        </h3>
                        <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-gray-500">Status atual</p>
                            <span className={`text-xs flex items-center gap-1 ${contract.status === 'active' ? 'text-green-500' :
                                contract.status === 'ended' ? 'text-red-500' : 'text-yellow-500'
                                }`}>
                                ↗ {contract.status === 'active' ? 'Ativo' : contract.status === 'ended' ? 'Expirado' : 'Aguardando'}
                            </span>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border-gray-800 backdrop-blur-sm hover:border-indigo-500/50 transition-all duration-300">
                    <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 rounded-xl bg-indigo-500/10 text-indigo-400">
                                <Clock className="h-6 w-6" />
                            </div>
                        </div>
                        <p className="text-sm text-gray-400">Duração Total</p>
                        <h3 className="text-2xl font-bold text-white mt-1">{totalDuration} {isDaysContract ? 'dias' : 'meses'}</h3>
                        <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-gray-500">Contrato de {totalDuration} {isDaysContract ? 'dias' : 'meses'}</p>
                            <span className="text-xs text-green-500 flex items-center gap-1">
                                ↗ Vigente
                            </span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Payments Section */}
            <div>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-white">Pagamentos</h2>
                        <p className="text-gray-400">Gerencie suas solicitações de pagamento</p>
                    </div>
                    <Button
                        onClick={() => setIsModalOpen(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/20"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Nova Solicitação
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <Card className="bg-gray-900/30 border-gray-800">
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-400">{isDaysContract ? 'Valor Total' : 'Valor Mensal'}</p>
                                <h3 className="text-xl font-bold text-white">{formatCurrency(isDaysContract ? contract.monthlyValue : totalContractValue / (totalDuration || 1))}</h3>
                            </div>
                            <div className="p-3 rounded-lg bg-green-500/10 text-green-500">
                                <DollarSign className="h-5 w-5" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gray-900/30 border-gray-800">
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-400">Valor Pago</p>
                                <h3 className="text-xl font-bold text-white">{formatCurrency(totalPaid)}</h3>
                            </div>
                            <div className="p-3 rounded-lg bg-yellow-500/10 text-yellow-500">
                                <TrendingUp className="h-5 w-5" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gray-900/30 border-gray-800">
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-400">Solicitações</p>
                                <h3 className="text-xl font-bold text-white">{pendingRequests}</h3>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                                <FileText className="h-5 w-5" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gray-900/30 border-gray-800">
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-400">Status</p>
                                <h3 className="text-xl font-bold text-white">
                                    {(() => {
                                        const day = new Date().getDate();
                                        if (day >= 1 && day <= 3) return 'Solicitação Disponível';
                                        if (day >= 6 && day <= 10) return 'Pagamento Emitido';
                                        return 'Em breve';
                                    })()}
                                </h3>
                            </div>
                            <div className="p-3 rounded-lg bg-purple-500/10 text-purple-500">
                                <Wallet className="h-5 w-5" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* History */}
                <Card className="bg-gray-900/50 border-gray-800">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white">
                            <History className="h-5 w-5 text-gray-400" />
                            Histórico de Pagamentos
                        </CardTitle>
                        <p className="text-sm text-gray-400">Acompanhe todas as suas solicitações de pagamento</p>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {userPayments.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    Nenhuma solicitação encontrada
                                </div>
                            ) : (
                                userPayments.map((payment) => (
                                    <div key={payment.id} className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50 hover:border-gray-600 transition-colors">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-lg bg-purple-500/10 text-purple-400">
                                                    <Wallet className="h-6 w-6" />
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-white">{formatCurrency(payment.totalValue)}</h4>
                                                    <p className="text-sm text-gray-400">
                                                        Solicitado: {new Date(payment.createdAt).toLocaleString()}
                                                    </p>
                                                    {(payment.status === 'approved' || payment.status === 'rejected') && payment.processedAt && (
                                                        <p className="text-xs text-gray-500">
                                                            {payment.status === 'approved' ? 'Aprovado' : 'Rejeitado'} em: {new Date(payment.processedAt).toLocaleString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {payment.invoiceSent && (
                                                    <Badge variant="outline" className="text-green-400 border-green-500/20 bg-green-500/10">
                                                        NF Enviada
                                                    </Badge>
                                                )}
                                                <StatusBadge status={payment.status} />
                                            </div>
                                        </div>

                                        {/* Detalhes extras */}
                                        <div className="pl-[72px] space-y-2">
                                            {payment.observation && (
                                                <p className="text-sm text-gray-400">
                                                    <span className="text-gray-500 font-bold">Descrição:</span> {payment.observation}
                                                </p>
                                            )}
                                            {payment.status === 'rejected' && payment.rejectionReason && (
                                                <div className="bg-red-500/10 border border-red-500/20 rounded p-2 mt-2">
                                                    <p className="text-sm text-red-300">
                                                        <span className="font-bold text-red-400">Motivo da Rejeição:</span> {payment.rejectionReason}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Data Display Modal */}
            {isDataModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl p-6 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Meus Dados de Prestador</h3>
                            <button onClick={() => setIsDataModalOpen(false)} className="text-gray-400 hover:text-white">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="flex border-b border-gray-800 mb-6 gap-6">
                            <button
                                onClick={() => setActiveTab('personal')}
                                className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'personal' ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                            >
                                Pessoais
                                {activeTab === 'personal' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t-full" />}
                            </button>
                            <button
                                onClick={() => setActiveTab('business')}
                                className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'business' ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                            >
                                Empresa
                                {activeTab === 'business' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t-full" />}
                            </button>
                            <button
                                onClick={() => setActiveTab('banking')}
                                className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'banking' ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                            >
                                Bancários
                                {activeTab === 'banking' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t-full" />}
                            </button>
                            <button
                                onClick={() => setActiveTab('contract')}
                                className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'contract' ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                            >
                                Contrato
                                {activeTab === 'contract' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t-full" />}
                            </button>
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`pb-2 text-sm font-medium transition-colors relative ${activeTab === 'profile' ? 'text-blue-400' : 'text-gray-400 hover:text-white'}`}
                            >
                                Perfil
                                {activeTab === 'profile' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t-full" />}
                            </button>
                        </div>

                        {renderDataTab()}

                        <div className="mt-8 flex justify-end gap-3">
                            <Button
                                onClick={() => setIsDataModalOpen(false)}
                                variant="outline"
                                className="border-gray-700 text-gray-300 hover:bg-gray-800"
                            >
                                Fechar
                            </Button>
                            {isDataRequestPending && dataUpdateRequest?.types?.includes(activeTab) && (
                                <Button
                                    onClick={handleSaveData}
                                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0"
                                >
                                    Salvar Alterações
                                    <CheckCircle2 className="w-4 h-4 ml-2" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Data Update Request Modal - Forces user to update */}
            {/* Data Update Request Modal - Forces user to update */}
            {dataRequestModalOpen && isDataRequestPending && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
                    <div className="relative w-full max-w-2xl">
                        {/* Ambient Glow */}
                        <div className="absolute -top-20 -left-20 w-64 h-64 bg-yellow-500/20 rounded-full blur-[100px] pointer-events-none" />
                        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-purple-500/20 rounded-full blur-[100px] pointer-events-none" />

                        <div className="relative bg-[#09090b] rounded-2xl border border-yellow-500/20 overflow-hidden shadow-2xl">
                            {/* Decorative Top Border */}
                            <div className="h-1 w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500" />

                            <div className="p-8 md:p-10">
                                <div className="flex flex-col items-center text-center space-y-6">
                                    {/* Icon */}
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-yellow-500/20 blur-xl rounded-full" />
                                        <div className="relative h-20 w-20 bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-yellow-500/30 flex items-center justify-center shadow-xl">
                                            <AlertTriangle className="h-10 w-10 text-yellow-500" />
                                        </div>
                                    </div>

                                    {/* Text */}
                                    <div className="space-y-4 max-w-lg">
                                        <h3 className="text-3xl font-bold text-white tracking-tight">
                                            Atualização Necessária
                                        </h3>
                                        <p className="text-gray-400 text-lg leading-relaxed">
                                            Para continuarmos nossa parceria com segurança, precisamos confirmar alguns dados do seu perfil.
                                        </p>
                                    </div>

                                    {/* Required Fields */}
                                    <div className="flex flex-wrap justify-center gap-2">
                                        {dataUpdateRequest.types.map(type => (
                                            <Badge key={type} variant="outline" className="bg-yellow-500/5 text-yellow-500 border-yellow-500/20 px-3 py-1 text-sm">
                                                {type === 'personal' ? 'Dados Pessoais' :
                                                    type === 'business' ? 'Dados da Empresa' :
                                                        (type === 'banking' || type === 'financial') ? 'Dados Bancários' :
                                                            type === 'contract' ? 'Contrato' : 'Perfil'}
                                            </Badge>
                                        ))}
                                    </div>

                                    {/* Action */}
                                    <div className="pt-4 w-full max-w-xs">
                                        <Button
                                            onClick={() => {
                                                setDataRequestModalOpen(false);
                                                setShowUpdateOnboarding(true);
                                            }}
                                            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold h-12 text-lg shadow-lg shadow-yellow-500/20 transition-all hover:scale-[1.02]"
                                        >
                                            Atualizar Agora
                                        </Button>
                                        <p className="text-xs text-gray-500 mt-4">
                                            Leva menos de 2 minutos
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Nova Solicitação */}
            {
                isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg p-6 animate-in zoom-in-95 duration-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-white">Solicitar Pagamento</h3>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-sm font-medium text-gray-400">Valor Padrão</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="skipStandard"
                                                checked={formData.skipStandardValue}
                                                onChange={(e) => setFormData({ ...formData, skipStandardValue: e.target.checked })}
                                                className="w-3 h-3 rounded border-gray-600 bg-gray-700 text-purple-600"
                                            />
                                            <label htmlFor="skipStandard" className="text-xs text-gray-400 cursor-pointer select-none">
                                                Não solicitar valor padrão
                                            </label>
                                        </div>
                                    </div>
                                    <div className={`p-3 bg-gray-800 rounded-lg text-gray-300 border border-gray-700 ${formData.skipStandardValue ? 'opacity-50 line-through' : ''}`}>
                                        {formatCurrency(contract.monthlyValue || 0)}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Valor Extra (opcional)</label>
                                    <input
                                        type="number"
                                        value={formData.extraValue}
                                        onChange={(e) => setFormData({ ...formData, extraValue: e.target.value })}
                                        className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                        placeholder="0,00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Valor Total</label>
                                    <div className="p-3 bg-gray-800/50 rounded-lg text-green-400 border border-gray-800 font-bold">
                                        {formatCurrency((formData.skipStandardValue ? 0 : (contract.monthlyValue || 0)) + (parseFloat(formData.extraValue) || 0))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Observações (opcional)</label>
                                    <textarea
                                        value={formData.observation}
                                        onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                                        className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none h-24"
                                        placeholder="Adicione observações sobre esta solicitação..."
                                    />
                                </div>

                                <a
                                    href="https://forms.gle/ZmUdgai6Ei5zvjoh8"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full p-4 rounded-lg border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all font-medium"
                                >
                                    <FileText className="h-5 w-5" />
                                    Anexar Nota Fiscal
                                </a>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="invoice-check"
                                        checked={formData.confirmedInvoice}
                                        onChange={(e) => setFormData({ ...formData, confirmedInvoice: e.target.checked })}
                                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-600"
                                    />
                                    <label htmlFor="invoice-check" className="text-sm text-gray-300">
                                        Confirmo que enviei a nota fiscal
                                    </label>
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <Button
                                        type="button"
                                        className="flex-1 bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700 text-white border-0"
                                        onClick={() => setIsModalOpen(false)}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/20"
                                        disabled={!formData.confirmedInvoice}
                                    >
                                        Solicitar
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default ServiceProvision;
